import React from 'react';
import { ReactFC } from '../../types/react/ReactFC';
import { EnrollmentPackageId, PersonDocumentId } from '../../types/api/primaryKeys/PrimaryKey';
import { ContentCard } from '../Cards';
import { Button, Form, FormGroup, Table } from 'react-bootstrap';
import { useState } from 'react';
import { Nullable } from '../../types/custom/Nullable';
import { useInitialDataFromApiCall } from '../../hooks/api/apiHooks';
import { getEmployeePackageDocuments, addEmployeePackageDocument, deleteEmployeePackageDocument } from '../../apiCalls/documentUploads/documentUploadApiCalls';
import { FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';
import DeleteConfirm from '../DeleteConfirm/DeleteConfirm';
import { useReduxDispatch } from '../../hooks/redux/useReduxDispatch';
import { stepNumberHelper } from '../../utils/StepNumberHelper';
import { useHistory } from 'react-router-dom';
import { PackageStepType } from '../../types/api/enrollmentPackages/Package';
import { fetchUpdatePackageProgress } from '../../actions/EnrollmentActions';
import { useWolfTracsState } from '../../hooks/redux/useWolfTracsState';
import { useRef } from 'react';
import { useNotifications } from '../../hooks/notifications/useNotifications';

interface Props {
    packageId: EnrollmentPackageId;
}

function toBase64(file: File) {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            let encoded = reader.result!.toString().replace(/^data:(.*,)?/, '');
            if ((encoded.length % 4) > 0) {
              encoded += '='.repeat(4 - (encoded.length % 4));
            }
            resolve(encoded);
        };
        reader.onerror = error => reject(error);
    });
}

export const DocumentUpload: ReactFC<Props> = ({ packageId }) => {
    const [ showUploadFields, setShowUploadFields ] = useState(false);
    const [ isContinueLoading, setIsContinueLoading ] = useState(false);
    const [ isUploadingFile, setIsUploadingFile ] = useState(false);

    const [ fileName, setFileName ] = useState<Nullable<string>>(null);
    const [ isFileSelectionValid, setIsFileSelectionValid ] = useState(false);
    const fileUploadRef = useRef<HTMLInputElement>(null);

    const { data, reFetchData } = useInitialDataFromApiCall(() => getEmployeePackageDocuments(packageId), [packageId]);

    const { data: activePackage } = useWolfTracsState(s => s.packages.activePackage);
    const dispatch = useReduxDispatch();

    const history = useHistory();

    const { notifyError } = useNotifications();

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // File must be selected, for now only support one file at a time.
        if (e.target.files !== null && e.target.files.length === 1) {
            setIsFileSelectionValid(true);
            return;
        }

        setIsFileSelectionValid(false);
    };

    const onShowUploadFieldsClick = () => {
        setShowUploadFields(true);
    };

    const onCancelClick = () => {
        setShowUploadFields(false);
        setFileName(null);
    };

    const onDeleteDocumentClick = async (personDocumentId: PersonDocumentId) => {
        await deleteEmployeePackageDocument(packageId, personDocumentId);
        await reFetchData();
    };

    const onBackClick = () => {
        // Hard coding this isn't ideal, but anything else would require a massive refactor. The fact the routing logic is copy/pasted everywhere is a bit wild.
        history.push(`/packages/${packageId}/dependents`);
    };

    const onUploadFileClick = async () => {
        if (fileUploadRef.current === null) {
            throw new Error('Could not find file');
        }

        // Not DRY, but not a huge deal.
        if (fileName === null || fileName === '') {
            throw new Error('Invalid File Name');
        }

        if (fileUploadRef.current.files === null || fileUploadRef.current.files.length !== 1) {
            throw new Error('Invalid file selection.');
        }

        const fileToUpload = fileUploadRef.current.files[0];

        const fileSizeInMb = fileToUpload.size / 1024 / 1024;

        // Arbitrary limit for now.
        if (fileSizeInMb > 40) {
            throw new Error('File is too large!');
        }

        setIsUploadingFile(true);

        let wasUploadSuccessful = false;

        const b64 = await toBase64(fileToUpload);

        try {
            await addEmployeePackageDocument(packageId, {
                b64: b64,
                actualFileNameAndExtension: fileToUpload.name,
                userSetFileName: fileName
            });

            wasUploadSuccessful = true;
        }
        catch {
            notifyError('Document failed to upload.');
        }
        finally {
            setIsUploadingFile(false);
        }

        if (wasUploadSuccessful) {
            await reFetchData();
            onCancelClick();
        }
    };

    const onContinueClick = async () => {
        if (activePackage === null) {
            throw new Error('Package data must be loaded to continue.');
        }

        const currentStepNumber = stepNumberHelper.stepNumberForNamedStep(activePackage, PackageStepType.DocumentUpload);

        if (currentStepNumber === null) {
            throw new Error('Unable to determine step sequence.');
        }

        setIsContinueLoading(true);

        try {
            await dispatch(fetchUpdatePackageProgress(packageId, currentStepNumber, true));
        }
        finally {
            setIsContinueLoading(false);
        }


        const nextStep = stepNumberHelper.getNextStep(activePackage, currentStepNumber);

        // Note: I feel pretty bad about copy pasting this into here. This is the third place this code now lives. Note that there are subtle differences in each spot though,
        // making this tricky and more time consuming than I can allow for right now to fix. Just know if the below gets modified, so should (probably) two other code spots.
        // Run a search for similar code to find them.
        // This is a little hacky.
        // Basically: Check for the next step. If the next step has an entity id,
        // we are assuming it's a dynamic step that we can route to.
        // If it's not a dynamic step, we will hard code in a route to insurability.
        if (nextStep) {
            // Route to a dynamic step.
            if(nextStep.harmonyProductGroup) {
                history.push(`/packages/${packageId}/harmony/${nextStep.harmonyProductGroup}`);
            } else if (nextStep.relatedEntityId) {
                history.push(`/packages/${packageId}/plantypes/${nextStep.relatedEntityId}`);
            } else if (nextStep.type === PackageStepType.DocumentUpload) {
                history.push(`/packages/${packageId}/documents`);
            }
            else {
                history.push(`/packages/${packageId}/summary`);
            }
        }
    };

    ////////////////// Render

    const continueButtonLabel = data === null || data.length === 0 ? 'Continue. I have no documents to upload.' : 'Continue';
    const isUploadButtonDisabled = !showUploadFields || fileName === null || fileName === '' || !isFileSelectionValid || isUploadingFile;

    return (
        <>
            <ContentCard>

                <h2>Employee Documents</h2>
                <p>Your employer may require you to upload benefit related documents as part of your online enrollment process.  Please add those documents here for your benefit administration team.</p>
                { !showUploadFields && <Button variant="success" onClick={onShowUploadFieldsClick}>Upload New Document</Button> }

                { showUploadFields &&
                    <div>
                        <Form>
                            <FormGroup controlId="document-name">
                                <Form.Label>Document Name*</Form.Label>
                                <Form.Control onChange={e => setFileName(e.target.value)} placeholder="Please provide a descriptive name for this document (required)" type="text" value={fileName ?? ''} />
                            </FormGroup>

                            <FormGroup>
                                <Form.Label>File*</Form.Label>
                                <input className="form-control" onChange={onFileInputChange} ref={fileUploadRef} type="file" accept="image/*,.pdf,.docx" />
                                <p>Only image, .pdf, and .docx files are allowed</p>
                            </FormGroup>
                            
                            <Button variant="secondary" onClick={onCancelClick}>Cancel</Button>
                            <Button disabled={isUploadButtonDisabled} onClick={onUploadFileClick} className="ml-2">{isUploadingFile ? 'Uploading...' : 'Upload'}</Button>
                        </Form>

                    </div>
                }

            </ContentCard>

            <ContentCard>
                <h3>Uploaded Documents</h3>

                { data === null || data.length === 0 ? <p>No Documents Uploaded</p> :

                <Table>
                    <tr>
                        <th>Document Name</th>
                        <th />
                    </tr>

                    {data.map(d => {
                        return (
                            <tr key={d.personDocumentId}>
                                <td>{d.documentName}</td>
                                <td><DeleteConfirm onDeleteClick={() => onDeleteDocumentClick(d.personDocumentId)} /></td>
                            </tr>
                        );
                    })}
                    
                </Table>
                }
            </ContentCard>

            <Button className="btn-default" onClick={onBackClick}><FaAngleDoubleLeft className="mb-1" /> Back</Button>
            <Button disabled={activePackage === null || isContinueLoading} onClick={onContinueClick} className="float-right">{continueButtonLabel} <FaAngleDoubleRight /></Button>
        </>
    );
};